/**
 * AuthContext — Global authentication state provider.
 * Uses createAuthProvider factory from @puglieseweb/shared-react-auth.
 * Centralizes API auth initialization for all services.
 */

import {
  createAuthProvider,
} from '@puglieseweb/shared-react-auth';

// API services — auth is initialized centrally here
import { specsApi } from '@/services/specsApi';
import { templatesApi } from '@/services/templatesApi';
import { docsApi } from '@/services/docsApi';
import { sanitizeApi } from '@/services/sanitizeApi';
import { githubApi } from '@/services/githubApi';
import { aiApi } from '@/services/aiApi';
import { stalenessApi } from '@/services/stalenessApi';
import { issuesApi } from '@/services/issuesApi';
import { scannerApi } from '@/services/scannerApi';
import { workflowApi } from '@/services/workflowApi';
import { planningApi } from '@/services/planningApi';
import { changesApi } from '@/services/changesApi';
import { guardrailsApi } from '@/services/guardrailsApi';
import { standardsApi } from '@/services/standardsApi';
import { techStackApi } from '@/services/techStackApi';
import { orgContextApi } from '@/services/orgContextApi';
import { rulesPromptsApi } from '@/services/rulesPromptsApi';
import { tenantApi } from '@/services/tenantApi';
import { apiKeysApi } from '@/services/apiKeysApi';
import { avatarApi } from '@/services/avatarApi';
import { mediaApi } from '@/services/mediaApi';
import { spacesApi } from '@/services/spacesApi';
import { projectsApi } from '@/services/projectsApi';
import { fitnessApi } from '@/services/fitnessApi';
import { skillsExportApi } from '@/services/skillsExportApi';
import { skillsApi } from '@/services/skillsApi';
import { teamSetupApi } from '@/services/teamSetupApi';
import { notificationsApi } from '@/services/notificationsApi';
import { captureBuffersApi } from '@/services/captureBuffersApi';
import { userPrefsApi } from '@/services/userPrefsApi';
import { clearSessionResidue } from '@/lib/sessionResidue';

// SignInAgainButton: the one AAL2 step-up (ADR-061 Amendment 18), bound to
// this provider's logout. It signs out of this app only and opens
// /login?front-door=in-app, coming back to the page it was drawn on.
export const {
  AuthProvider,
  useAuthContext,
  LoginPage,
  ForgotPasswordPage,
  SignInAgainButton,
} = createAuthProvider({
  // Capture workflow state and plan history are the person's, not the
  // browser's. onLoggedOut runs after the provider sign-out, success or not.
  authOptions: { onLoggedOut: clearSessionResidue },
  onAuthenticated: (getAccessToken) => {
    specsApi.setAuthProvider(getAccessToken);
    templatesApi.setAuthProvider(getAccessToken);
    docsApi.setAuthProvider(getAccessToken);
    sanitizeApi.setAuthProvider(getAccessToken);
    githubApi.setAuthProvider(getAccessToken);
    aiApi.setAuthProvider(getAccessToken);
    stalenessApi.setAuthProvider(getAccessToken);
    issuesApi.setAuthProvider(getAccessToken);
    scannerApi.setAuthProvider(getAccessToken);
    workflowApi.setAuthProvider(getAccessToken);
    planningApi.setAuthProvider(getAccessToken);
    changesApi.setAuthProvider(getAccessToken);
    guardrailsApi.setAuthProvider(getAccessToken);
    standardsApi.setAuthProvider(getAccessToken);
    techStackApi.setAuthProvider(getAccessToken);
    orgContextApi.setAuthProvider(getAccessToken);
    rulesPromptsApi.setAuthProvider(getAccessToken);
    tenantApi.setAuthProvider(getAccessToken);
    apiKeysApi.setAuthProvider(getAccessToken);
    avatarApi.setAuthProvider(getAccessToken);
    mediaApi.setAuthProvider(getAccessToken);
    spacesApi.setAuthProvider(getAccessToken);
    projectsApi.setAuthProvider(getAccessToken);
    fitnessApi.setAuthProvider(getAccessToken);
    skillsExportApi.setAuthProvider(getAccessToken);
    skillsApi.setAuthProvider(getAccessToken);
    teamSetupApi.setAuthProvider(getAccessToken);
    notificationsApi.setAuthProvider(getAccessToken);
    captureBuffersApi.setAuthProvider(getAccessToken);
    userPrefsApi.setAuthProvider(getAccessToken);
  },
  pages: {
    brandName: 'SystemDox',
    signupPath: '/signup',
    forgotPasswordPath: '/forgot-password',
    loginPath: '/login',
    // Post-login launch page: pick org, then Console or Documentation (#564)
    redirectPath: '/launch',
  },
});
